import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://tracker-api.yg1.org'

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('publisherToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('publisherToken')
      localStorage.removeItem('publisher')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export interface Publisher {
  id: string
  email: string
  username: string
  companyName?: string
  fullName?: string
  status: 'pending' | 'active' | 'suspended' | 'banned'
  tier: 'starter' | 'pro' | 'vip'
  totalEarnings: number
  pendingPayment: number
  lastPaymentDate?: string
  createdAt: string
}

export interface Campaign {
  id: string
  name: string
  description?: string
  category: string
  countries: string[]
  payout: number
  payoutType: 'cpa' | 'cpl' | 'cps' | 'revshare'
  trackingUrl: string
  previewUrl: string
  status: 'active' | 'paused'
}

export interface Stats {
  totalClicks: number
  uniqueClicks: number
  conversions: number
  conversionRate: number
  earnings: number
  pendingPayment: number
  lastPaymentDate?: string
  topCampaigns: Campaign[]
}

export interface PaymentHistory {
  id: string
  amount: number
  method: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  transactionId?: string
  notes?: string
  requestedAt: string
  processedAt?: string
}

export interface Balance {
  available: number
  pending: number
  lifetime: number
}

export interface PaymentMethod {
  type: 'wire' | 'paypal' | 'crypto'
  details: {
    [key: string]: string
  }
}

// Helper functions for generating demo data
const generateHourlyStats = () => {
  const hours = []
  for (let i = 0; i < 24; i++) {
    hours.push({
      hour: `${i}:00`,
      clicks: Math.floor(Math.random() * 200) + 50,
      conversions: Math.floor(Math.random() * 10) + 1,
    })
  }
  return hours
}

const generateDailyStats = (days: number) => {
  const stats = []
  const now = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now)
    date.setDate(date.getDate() - i)
    stats.push({
      date: date.toISOString().split('T')[0],
      clicks: Math.floor(Math.random() * 500) + 200,
      uniqueClicks: Math.floor(Math.random() * 300) + 100,
      conversions: Math.floor(Math.random() * 20) + 5,
      earnings: Math.random() * 100 + 20,
    })
  }
  return stats
}

// Demo data for testing
const demoData = {
  campaigns: [
    {
      id: '1',
      name: 'Premium VPN Service',
      description: 'High-speed VPN with global servers',
      category: 'Technology',
      countries: ['US', 'UK', 'CA'],
      payout: 45,
      payoutType: 'cpa' as const,
      trackingUrl: 'https://r.yg1.org/demo1?pub=YOURID',
      previewUrl: 'https://example.com/vpn',
      status: 'active' as const,
    },
    {
      id: '2',
      name: 'Fitness App Subscription',
      description: 'Complete workout and nutrition tracking',
      category: 'Health & Fitness',
      countries: [],
      payout: 35,
      payoutType: 'cpl' as const,
      trackingUrl: 'https://r.yg1.org/demo2?pub=YOURID',
      previewUrl: 'https://example.com/fitness',
      status: 'active' as const,
    },
    {
      id: '3',
      name: 'Online Trading Platform',
      description: 'Trade stocks, crypto, and forex',
      category: 'Finance',
      countries: ['US', 'UK'],
      payout: 250,
      payoutType: 'cpa' as const,
      trackingUrl: 'https://r.yg1.org/demo3?pub=YOURID',
      previewUrl: 'https://example.com/trading',
      status: 'active' as const,
    },
  ],
  stats: {
    totalClicks: 4532,
    uniqueClicks: 2341,
    conversions: 47,
    conversionRate: 2.01,
    earnings: 1250.5,
    pendingPayment: 450.0,
    lastPaymentDate: '2024-01-15',
    topCampaigns: [],
  },
  balance: {
    available: 450.0,
    pending: 125.5,
    lifetime: 4750.25,
  },
  paymentMethods: [
    {
      type: 'paypal' as const,
      details: { email: 'user@example.com' },
    },
  ],
  paymentRequests: [],
}

const isDemoMode = () =>
  window.location.hostname === 'localhost' || window.location.hostname.includes('pages.dev')

export const publisherApi = {
  async signup(data: {
    email: string
    username: string
    password: string
    firstName?: string
    lastName?: string
    companyName?: string
    phone?: string
    website?: string
    country?: string
  }) {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return { success: true }
    }
    const response = await api.post('/pub/signup', data)
    return response.data
  },

  async login(email: string, password: string) {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return {
        publisher: {
          id: 'demo-user',
          email,
          username: email.split('@')[0],
          status: 'active',
          tier: 'starter',
          totalEarnings: 1250.5,
          pendingPayment: 450.0,
          createdAt: new Date().toISOString(),
        },
        token: 'demo-token',
      }
    }
    const response = await api.post('/pub/login', { email, password })
    return response.data
  },

  async getProfile() {
    if (isDemoMode()) {
      const stored = localStorage.getItem('publisher')
      const base = stored ? JSON.parse(stored) : {}
      return {
        id: base.id || 'demo-user',
        email: base.email || 'demo@example.com',
        username: base.username || 'demouser',
        fullName: base.fullName || 'Demo User',
        company: base.company || '',
        website: base.website || '',
        phone: base.phone || '',
        country: base.country || 'US',
        timezone: base.timezone || 'America/New_York',
        language: base.language || 'en',
        status: base.status || 'active',
        tier: base.tier || 'starter',
        emailVerified: true,
        twoFactorEnabled: false,
        apiKey: 'pk_live_demo_xxxxxxxxxxxxxxxxxx',
        webhookUrl: '',
        notificationPreferences: {
          emailAlerts: true,
          paymentNotifications: true,
          performanceReports: false,
          newsletterUpdates: false,
        },
        createdAt: base.createdAt || '2024-01-01T00:00:00Z',
        lastLoginAt: new Date().toISOString(),
        ...base,
      }
    }
    const response = await api.get<Publisher>('/pub/profile')
    return response.data
  },

  async updateProfile(data: Partial<Publisher>) {
    if (isDemoMode()) {
      const stored = localStorage.getItem('publisher')
      const publisher = stored ? JSON.parse(stored) : {}
      const updated = { ...publisher, ...data }
      localStorage.setItem('publisher', JSON.stringify(updated))
      return updated
    }
    const response = await api.patch<Publisher>('/pub/profile', data)
    return response.data
  },

  async getStats() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return demoData.stats
    }
    const response = await api.get<Stats>('/pub/stats')
    return response.data
  },

  async getCampaigns() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return demoData.campaigns
    }
    const response = await api.get<Campaign[]>('/pub/campaigns')
    return response.data
  },

  async requestPayment(data: { amount: number; method: string; notes?: string }) {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      const request = {
        id: `req-${Date.now()}`,
        amount: data.amount,
        method: data.method,
        status: 'pending' as const,
        requestedAt: new Date().toISOString(),
        notes: data.notes,
      }
      // Store in localStorage for persistence
      const requests = JSON.parse(localStorage.getItem('paymentRequests') || '[]')
      requests.push(request)
      localStorage.setItem('paymentRequests', JSON.stringify(requests))
      return request
    }
    const response = await api.post('/pub/payment-request', data)
    return response.data
  },

  async getPaymentHistory() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return JSON.parse(localStorage.getItem('paymentRequests') || '[]')
    }
    const response = await api.get<PaymentHistory[]>('/pub/payments')
    return response.data
  },

  async getBalance() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 200))
      return demoData.balance
    }
    const response = await api.get<Balance>('/pub/balance')
    return response.data
  },

  async getPaymentRequests() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      return JSON.parse(localStorage.getItem('paymentRequests') || '[]')
    }
    const response = await api.get<PaymentHistory[]>('/pub/payment-requests')
    return response.data
  },

  async getPaymentMethods() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 200))
      return demoData.paymentMethods
    }
    const response = await api.get<PaymentMethod[]>('/pub/payment-methods')
    return response.data
  },

  async getStatistics(timeRange: '24h' | '7d' | '30d' | '90d') {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 300))
      const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
      return {
        totalClicks: Math.floor(Math.random() * 10000) + 2000,
        uniqueClicks: Math.floor(Math.random() * 5000) + 1000,
        conversions: Math.floor(Math.random() * 200) + 20,
        conversionRate: Math.random() * 5 + 1,
        earnings: Math.random() * 2000 + 500,
        averageEPC: Math.random() * 0.5 + 0.1,
        topCountries: [
          { country: 'United States', clicks: 2341, earnings: 450.25 },
          { country: 'United Kingdom', clicks: 1523, earnings: 325.5 },
          { country: 'Canada', clicks: 987, earnings: 210.75 },
          { country: 'Australia', clicks: 654, earnings: 145.0 },
          { country: 'Germany', clicks: 432, earnings: 95.25 },
        ],
        topDevices: [
          { device: 'Mobile', percentage: 65 },
          { device: 'Desktop', percentage: 30 },
          { device: 'Tablet', percentage: 5 },
        ],
        dailyStats: generateDailyStats(days),
        hourlyStats: generateHourlyStats(),
        browserStats: [
          { browser: 'Chrome', clicks: 3245, percentage: 45 },
          { browser: 'Safari', clicks: 2156, percentage: 30 },
          { browser: 'Firefox', clicks: 1078, percentage: 15 },
          { browser: 'Edge', clicks: 719, percentage: 10 },
        ],
        osStats: [
          { os: 'iOS', clicks: 2876, percentage: 40 },
          { os: 'Android', clicks: 2517, percentage: 35 },
          { os: 'Windows', clicks: 1438, percentage: 20 },
          { os: 'MacOS', clicks: 359, percentage: 5 },
        ],
      }
    }
    const response = await api.get(`/pub/statistics?range=${timeRange}`)
    return response.data
  },

  async updatePassword(data: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }) {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return { success: true }
    }
    const response = await api.post('/pub/change-password', data)
    return response.data
  },

  async regenerateApiKey() {
    if (isDemoMode()) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return { apiKey: `pk_live_demo_${Math.random().toString(36).substring(7)}` }
    }
    const response = await api.post('/pub/regenerate-api-key')
    return response.data
  },
}
